import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { RecordFormModal } from "@/components/RecordFormModal";
import { ArchiveControls } from "@/components/ArchiveControls";
import { StatusChip } from "@/components/StatusChip";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { DetailSkeleton } from "@/components/Skeletons";
import { Section, SectionPanel } from "@/components/Section";
import { RecordPageLayout, RecordTag } from "@/components/RecordPageLayout";
import AgencyAccessPanel from "@/pages/AgencyAccessPanel";
import { EstimatorEnabledToggle } from "@/components/EstimatorEnabledToggle";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";
import {
  Handshake, Mail, Phone, Pencil, ExternalLink, Star, MapPin, Globe, FolderOpen,
  Wallet, Clock, CalendarRange, ReceiptText, UserPlus, Copy,
} from "lucide-react";
import { toast } from "sonner";

// Email failure UX + manual link sharing (2026-09-28 follow-up) — mirrors Clients.jsx's
// PortalUsersSection exactly: `outcome !== "sent"` is always true in Preview
// (EMAIL_SENDS_DISABLED=true), and "Copy set-password link" is a PERMANENT fallback (not
// conditional on failure) since a "sent" outcome doesn't guarantee real-world delivery.
const EMAIL_OUTCOME_REASON = {
  skipped_not_configured: "emails are disabled in this environment",
  blocked_non_production: "blocked by the non-production email safeguard",
  failed: "delivery failed",
};

// Design System 2.0 (2026-10, Phase 5) — rebuilt on the shared RecordPageLayout (same header
// shape as Client/Contact Detail), DETAILS as a compact 2-column definition list (same 12
// fields, same values, same "—" fallback — just denser than the old 4-col card grid), Rate
// Card / Projects / Agency Access retokened onto Design System 2.0 tokens. Purge moved into
// the "⋯" overflow menu (was a top-level button). No API/data/gating/agency-linking change —
// same `/v1/partners/:id` call, same RecordFormModal, same AgencyAccessPanel untouched.
export default function PartnerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasScope } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [rateCard, setRateCard] = useState(null);
  const [editing, setEditing] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const guard = useEmailGuard();

  const load = () => api.get(`/v1/partners/${id}`)
    .then((r) => setData(r.data))
    .catch((e) => setError(e?.response?.status === 403 ? "restricted" : "not_found"));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const remote = data?.partner?.sync?.remote_id;
    if (!remote || !hasScope("partner_rate_card.admin")) return;
    api.get(`/v1/partner-portal/admin/rate-cards/${remote}`).then((r) => setRateCard(r.data)).catch(() => setRateCard(null));
  }, [data, hasScope]);

  if (error) return <div className="p-8 text-sm text-faint" data-testid="partner-detail-error">{error === "restricted" ? "Access Restricted" : "Partner not found"}</div>;
  if (!data) return <DetailSkeleton testId="partner-detail-skeleton" />;

  const p = data.partner;
  const hasCard = !!rateCard?.header?.version_no;
  // Phase 5 fix (2026-10, Job C2) — the "⋯" trigger must not render when its only item
  // (ArchiveControls) would be hidden for this user; same condition ArchiveControls itself uses.
  const hasPartnerOverflow = hasScope("partners.create") || user?.role === "founder";
  const isAgency = ["partner agency", "agency partner"].includes((p.partner_type || "").trim().toLowerCase());
  const inviteEmail = (p.login_emails || "").split(",")[0].trim();

  const sendInvite = async () => {
    if (!inviteEmail) { toast.error("Add a login email on this record first."); return; }
    setInviteBusy(true);
    try {
      const r = await api.post(`/v1/agency-auth/admin/send-invite`, { email: inviteEmail });
      // Email Send Guard (2026-02): the account/token mint above always happens; the actual
      // email (always outside @njmpro.com) waits for a human to confirm via the modal.
      if (guard.intercept(r.data, async (typedCount) => {
        const cr = await api.post(`/v1/agency-auth/admin/send-invite/confirm`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        toast.success(`Set-password invite sent to ${inviteEmail}.`);
        const outcome = cr.data?.email_result?.outcome;
        if (outcome && outcome !== "sent") {
          const reason = EMAIL_OUTCOME_REASON[outcome] || cr.data?.email_result?.error || "not sent";
          toast.warning(`Email not sent: ${reason}. Use "Copy set-password link" to share it manually.`);
        }
      })) return;
      toast.success(r.data?.message || `Set-password invite sent to ${inviteEmail}.`);
      const outcome = r.data?.email_result?.outcome;
      if (outcome && outcome !== "sent") {
        const reason = EMAIL_OUTCOME_REASON[outcome] || r.data?.email_result?.error || "not sent";
        toast.warning(`Email not sent: ${reason}. Use "Copy set-password link" to share it manually.`);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = d?.code;
      toast.error(code === "not_a_partner_agency" ? "This email isn't a login email on a Partner Agency record."
        : code === "account_conflict" ? "This email already has a different account type."
        : d?.message || d?.hint || "Couldn't send the invite. Please try again.");
    } finally { setInviteBusy(false); }
  };

  const copySetPasswordLink = async () => {
    if (!inviteEmail) { toast.error("Add a login email on this record first."); return; }
    setCopyBusy(true);
    try {
      const r = await api.post(`/v1/agency-auth/admin/send-invite`, { email: inviteEmail });
      const link = r.data?.email_result?.link;
      if (!link) { toast.error("No link available yet — try again."); return; }
      await navigator.clipboard.writeText(link);
      toast.success("Set-password link copied to clipboard");
      const outcome = r.data?.email_result?.outcome;
      if (outcome && outcome !== "sent") {
        const reason = EMAIL_OUTCOME_REASON[outcome] || r.data?.email_result?.error || "not sent";
        toast.warning(`Email not sent: ${reason}.`);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = d?.code;
      toast.error(code === "not_a_partner_agency" ? "This email isn't a login email on a Partner Agency record."
        : code === "account_conflict" ? "This email already has a different account type."
        : d?.message || d?.hint || "Couldn't get a link. Please try again.");
    } finally { setCopyBusy(false); }
  };

  const DETAILS = [
    { label: "Location", value: [p.city, p.country].filter(Boolean).join(", "), icon: MapPin },
    { label: "Primary Contact", value: p.primary_contact, icon: Mail },
    { label: "Login Email", value: p.login_emails ? <a href={`mailto:${p.login_emails}`} className="hover:text-gold-text">{p.login_emails}</a> : null, icon: Mail },
    { label: "Phone", value: p.phone, icon: Phone },
    { label: "Website", value: p.website ? <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-gold-text">Visit <ExternalLink className="h-3 w-3" /></a> : null, icon: Globe },
    { label: "Resource Link (Drive)", value: p.resource_link ? <a href={p.resource_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-gold-text">Open <ExternalLink className="h-3 w-3" /></a> : null, icon: FolderOpen },
    { label: "Default Payment Terms", value: p.default_payment_terms, icon: ReceiptText },
    { label: "Time Zone", value: p.time_zone, icon: Clock },
    { label: "Agreement Period", value: (p.agreement_start_date_start || p.agreement_expiry_date_start) ? `${p.agreement_start_date_start || "—"} → ${p.agreement_expiry_date_start || "—"}` : null, icon: CalendarRange },
    { label: "GST Number", value: p.gst_number },
    { label: "Company Registration No.", value: p.company_registration_no },
    { label: "Notes", value: p.notes },
  ];

  return (
    <RecordPageLayout
      testId="partner-detail-page"
      titleTestId="partner-detail-name"
      archivedTestId="partner-archived-badge"
      icon={Handshake}
      title={p.name}
      statusChip={<StatusChip status={p.status} testId="partner-status-chip" />}
      tags={[p.partner_type, p.partner_geography, p.default_currency]}
      archived={p.archived}
      secondaryActions={<>
        {p.preferred_partner && (
          <span className="inline-flex items-center gap-1 rounded-full bg-tone-gold-bg px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-tone-gold-text" data-testid="partner-preferred-badge">
            <Star className="h-2.5 w-2.5 fill-current" /> Preferred
          </span>
        )}
        {hasScope("partners.create") && (
          <button type="button" onClick={() => setEditing(true)} data-testid="partner-edit-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-paper px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-gold-text">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
        {isAgency && hasScope("build.write") && (
          <button type="button" onClick={copySetPasswordLink} disabled={copyBusy || !inviteEmail} data-testid="agency-copy-set-password-link-btn"
            title={inviteEmail ? "Mint a fresh single-use link and copy it — share directly if the email never arrives" : "Add a login email first"}
            className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-paper px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-gold-text disabled:opacity-50 disabled:cursor-not-allowed">
            <Copy className="h-3.5 w-3.5" /> {copyBusy ? "Copying…" : "Copy set-password link"}
          </button>
        )}
      </>}
      primaryAction={isAgency && hasScope("build.write") && (
        <button type="button" onClick={sendInvite} disabled={inviteBusy || !inviteEmail} data-testid="agency-send-invite-btn"
          title={inviteEmail ? `Email a single-use set-password invite to ${inviteEmail}` : "Add a login email first"}
          className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-1.5 text-xs font-semibold text-paper hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed">
          <UserPlus className="h-3.5 w-3.5" /> {inviteBusy ? "Sending…" : "Send set-password invite"}
        </button>
      )}
      overflow={
        hasPartnerOverflow && (
          <ArchiveControls
            entity="partners" variant="menu"
            record={{ id: p.id, name: p.name, archived: p.archived }}
            canWrite={hasScope("partners.create")}
            isFounder={user?.role === "founder"}
            onChanged={load}
            onPurged={() => navigate("/partners")}
          />
        )
      }
    >
      <RecordFormModal
        type="partner"
        mode="edit"
        open={editing}
        onClose={() => setEditing(false)}
        recordId={p.id}
        initialValues={{
          name: p.name, partner_type: p.partner_type, status: p.status, partner_geography: p.partner_geography,
          default_currency: p.default_currency, preferred_partner: !!p.preferred_partner, city: p.city, country: p.country,
          gst_number: p.gst_number, company_registration_no: p.company_registration_no, login_emails: p.login_emails,
          email: p.email, phone: p.phone, primary_contact: p.primary_contact, website: p.website,
          resource_link: p.resource_link, default_payment_terms: p.default_payment_terms, time_zone: p.time_zone,
          agreement_start_date: p.agreement_start_date_start || p.agreement_start_date || null,
          agreement_expiry_date: p.agreement_expiry_date_start || p.agreement_expiry_date || null,
          notes: p.notes,
        }}
        onSaved={load}
      />

      <SectionPanel testId="partner-details-section" title="Details"
        action={hasScope("partners.create") && (
          <button type="button" onClick={() => setEditing(true)} data-testid="partner-details-edit-btn" className="text-xs font-medium text-gold-text hover:text-ink">Edit</button>
        )}
      >
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 p-4">
          {DETAILS.map((d) => (
            <div key={d.label}>
              <dt className="text-[11px] uppercase tracking-wider text-faint inline-flex items-center gap-1">{d.icon && <d.icon className="h-3 w-3" />} {d.label}</dt>
              <dd className="text-sm mt-0.5 text-ink break-words">{d.value || <span className="text-faint">—</span>}</dd>
            </div>
          ))}
        </dl>
      </SectionPanel>

      {hasScope("partner_rate_card.admin") && (
        <Section title="Rate Card" testId="partner-rate-card-section">
          <div className="rounded-md border border-hairline bg-paper">
            {!hasCard ? (
              <div className="p-6">
                <EmptyState title="No rate card yet" hint="Initialize a blank card or bootstrap from NJM's benchmark on the Partner Rate Cards admin queue." testId="partner-rate-card-empty" />
                <Link to="/admin/partner-rate-cards" data-testid="partner-rate-card-manage-link" className="mt-3 inline-flex items-center gap-1.5 text-xs text-gold-text hover:underline">
                  <Wallet className="h-3.5 w-3.5" /> Go to Partner Rate Cards →
                </Link>
              </div>
            ) : (
              <div className="p-4 space-y-3" data-testid="partner-rate-card-summary">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusChip status={rateCard.header.status} testId="partner-rate-card-summary-status" />
                    <span className="text-xs text-ink-soft">v{rateCard.header.version_no} · {rateCard.header.currency} · {rateCard.header.line_items} lines</span>
                  </div>
                  <Link to="/admin/partner-rate-cards" data-testid="partner-rate-card-manage-link" className="inline-flex items-center gap-1.5 text-xs text-gold-text hover:underline">
                    <Wallet className="h-3.5 w-3.5" /> Manage Rate Card
                  </Link>
                </div>
                {rateCard.header.completion && (
                  <div>
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="text-faint">Onboarding completion</span>
                      <span className="font-semibold text-ink" data-testid="partner-rate-card-summary-completion">{rateCard.header.completion.pct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-hairline overflow-hidden">
                      <div className="h-full rounded-full bg-brand" style={{ width: `${rateCard.header.completion.pct}%` }} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title="Projects Using This Partner" testId="partner-projects-section">
        <div className="rounded-md border border-hairline bg-paper">
          {data.projects.length === 0 ? (
            <EmptyState title="No projects assigned yet" hint="Assign this partner from a Project's Edit form (Production Model = Partner Managed)." testId="partner-projects-empty" />
          ) : (
            <div className="divide-y divide-hairline">
              {data.projects.map((proj) => (
                <div key={proj.id} className="flex items-center justify-between px-4 py-3">
                  <Link to={`/projects/${proj.id}`} data-testid={`partner-project-row-${proj.id}`} className="text-sm text-ink hover:text-gold-text">{proj.name}</Link>
                  <StatusChip status={proj.status} testId={`partner-project-status-${proj.id}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {isAgency && hasScope("client_portal.admin") && (
        <SectionPanel>
          <p className="text-[11px] uppercase tracking-wider text-[#78716C] mb-2">Agency Settings</p>
          <EstimatorEnabledToggle entity="partners" recordId={p.id} initialEnabled={p.estimator_enabled} />
        </SectionPanel>
      )}

      {isAgency && hasScope("build.write") && p.sync?.remote_id && (
        <AgencyAccessPanel agencyRemoteId={p.sync.remote_id} loginEmails={p.login_emails} agencyName={p.name} />
      )}
      <EmailConfirmModal
        open={guard.open}
        preview={guard.preview}
        busy={guard.busy}
        onConfirm={guard.confirm}
        onCancel={guard.cancel}
      />
    </RecordPageLayout>
  );
}
